<template>
  <div class="register-form">
    <form @submit.prevent="register">
      <div class="form-group">
        <label for="username">Username</label>
        <input type="text" id="username" v-model="username" @input="validateField('username')" required />
        <p v-if="usernameError" class="error-message">{{ usernameError }}</p>
      </div>
      <div class="form-group">
        <label for="email">Email</label>
        <input type="email" id="email" v-model="email" @input="validateField('email')" required />
        <p v-if="emailError" class="error-message">{{ emailError }}</p>
      </div>
      <div class="form-group">
        <label for="password">Password</label>
        <input type="password" id="password" v-model="password" required />
      </div>
      <div class="form-group">
       <label for="confirmPassword">Konfirmasi Password</label>
       <input type="password" id="confirmPassword" v-model="confirmPassword" required />
     </div>
     <div class="form-group">
       <label for="referralCode">Kode Referral (opsional)</label>
       <input type="text" id="referralCode" v-model="referralCode" />
     </div>
      <button type="submit" class="btn btn-primary" :disabled="isSubmitting || !usernameAvailable || !emailAvailable">
        {{ isSubmitting ? 'Memproses...' : 'Daftar' }}
      </button>
    </form>
    <p v-if="error" class="error-message">{{ error }}</p>
    <p v-if="success" class="success-message">{{ success }}</p>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';

export default {
  name: 'RegisterForm',
  props: {
    redirectAfterRegister: {
      type: String,
      default: '/login'
    },
    showLoginLink: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      referralCode: '',
      error: '',
      success: '',
      isSubmitting: false,
      usernameError: '',
      usernameAvailable: false,
      emailError: '',
      emailAvailable: false,
      usernameTimer: null,
      emailTimer: null
    };
  },
  methods: {
    async validateField(field) {
      if (field === 'username') {
        if (this.usernameTimer) clearTimeout(this.usernameTimer);

        if (!this.username) {
          this.usernameError = 'Username harus diisi';
          this.usernameAvailable = false;
          return;
        }

        if (this.username.length < 3) {
          this.usernameError = 'Username minimal 3 karakter';
          this.usernameAvailable = false;
          return;
        }

        if (/\s/.test(this.username)) {
          this.usernameError = 'Username tidak boleh mengandung spasi';
          this.usernameAvailable = false;
          return;
        }

        if (!/^[a-zA-Z0-9_]+$/.test(this.username)) {
          this.usernameError = 'Username hanya boleh berisi huruf, angka, dan underscore';
          this.usernameAvailable = false;
          return;
        }

        this.usernameError = '';

        this.usernameTimer = setTimeout(() => {
          this.checkUsernameAvailability();
        }, 500);
      } else if (field === 'email') {
        if (this.emailTimer) clearTimeout(this.emailTimer);

        if (!this.email) {
          this.emailError = 'Email harus diisi';
          this.emailAvailable = false;
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.email)) {
          this.emailError = 'Format email tidak valid';
          this.emailAvailable = false;
          return;
        }

        this.emailError = '';

        this.emailTimer = setTimeout(() => {
          this.checkEmailAvailability();
        }, 500);
      }
    },

    async checkUsernameAvailability() {
      try {
        this.usernameAvailable = false;
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/users/validate-field/`,
          {
            params: {
              field: 'username',
              value: this.username
            }
          }
        );

        if (response.data.is_taken) {
          this.usernameError = 'Username sudah digunakan, silakan pilih yang lain';
          this.usernameAvailable = false;
        } else {
          this.usernameError = '';
          this.usernameAvailable = true;
        }
      } catch (error) {
        console.error('Error checking username:', error);
        this.usernameError = 'Gagal memeriksa ketersediaan username';
        this.usernameAvailable = false;
      }
    },

    async checkEmailAvailability() {
      try {
        this.emailAvailable = false;
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/users/validate-field/`,
          {
            params: {
              field: 'email',
              value: this.email
            }
          }
        );

        if (response.data.is_taken) {
          this.emailError = 'Email sudah terdaftar, silakan gunakan email lain';
          this.emailAvailable = false;
        } else {
          this.emailError = '';
          this.emailAvailable = true;
        }
      } catch (error) {
        console.error('Error checking email:', error);
        this.emailError = 'Gagal memeriksa ketersediaan email';
        this.emailAvailable = false;
      }
    },

    async register() {
      if (this.password !== this.confirmPassword) {
        this.error = 'Password dan konfirmasi password tidak cocok.';
        return;
      }

      if (this.password.length < 8) {
        this.error = 'Password harus minimal 8 karakter.';
        return;
      }

      this.isSubmitting = true;
      this.error = '';

      try {
        // 1. Register user
        const registerResponse = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/users/`,
          {
            username: this.username,
            email: this.email,
            password: this.password,
            referral_code: this.referralCode
          }
        );

        if (registerResponse.data) {
          // 2. Auto login setelah register
          const loginResponse = await axios.post(
            `${process.env.VUE_APP_API_BASE_URL}/api/token/`,
            {
              username: this.username,
              password: this.password
            }
          );

          if (loginResponse.data && loginResponse.data.access) {
            // Use auth store instead of direct localStorage
            const authStore = useAuthStore();
            authStore.setTokens(loginResponse.data.access, loginResponse.data.refresh);
            authStore.username = this.username;
            authStore.role = loginResponse.data.role || 'registered';
            authStore.avatar = loginResponse.data.avatar || '';
            authStore.syncToLocalStorage();
            
            // Emit event register success dengan token
            this.$emit('register-success', { token: loginResponse.data.access });
            
            this.success = 'Pendaftaran berhasil!';
            
            // Reset form
            this.username = '';
            this.email = '';
            this.password = '';
            this.confirmPassword = '';

            // Jika ada redirectAfterRegister dan bukan dalam modal
            if (this.redirectAfterRegister && this.$router) {
              this.$router.push('/');
            }
          }
        }
      } catch (error) {
        if (error.response?.data) {
          if (error.response.data.username) {
            this.error = `Username sudah digunakan`;
          } else if (error.response.data.email) {
            this.error = `Email sudah terdaftar`;
          } else if (error.response.data.password) {
            this.error = error.response.data.password[0];
          } else {
            this.error = error.response.data.message || 'Terjadi kesalahan saat mendaftar';
          }
        } else {
          this.error = 'Terjadi kesalahan pada server';
        }
        this.success = '';
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.register-form {
  width: 100%;
}

.form-group {
  text-align: left;
  margin-bottom: 18px;
}

label {
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
  color: #333;
}

input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 15px;
  transition: border-color 0.3s;
}

input:focus {
  outline: none;
  border-color: #488e71;
  box-shadow: 0 0 0 2px rgba(72, 142, 113, 0.1);
}

.btn-primary {
  width: 100%;
  padding: 12px;
  background-color: #488e71;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  transition: background-color 0.3s;
}

.btn-primary:hover {
  background-color: #3a7259;
}

.btn-primary:disabled {
  background-color: #93c3b1;
  cursor: not-allowed;
}

.error-message {
  color: #e74c3c;
  margin-top: 15px;
  padding: 10px;
  border-radius: 5px;
  background-color: rgba(231, 76, 60, 0.1);
}

.success-message {
  color: #27ae60;
  margin-top: 15px;
  padding: 10px;
  border-radius: 5px;
  background-color: rgba(39, 174, 96, 0.1);
}
</style>
